import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { entitySubscriptionsQueryOptions } from '@/features/subscriptions'

import { currentCustomerPreferencesQueryOptions } from './notificationPreferences.service'
import { deriveAcknowledgementReceivers } from '../domain/deriveAcknowledgementReceivers'

type UseAcknowledgementReceiversInput = {
  referenceId?: string
  bookingRecipients?: string[]
}

export const useAcknowledgementReceivers = ({ referenceId, bookingRecipients }: UseAcknowledgementReceiversInput) => {
  const subscriptionsQuery = useQuery({
    ...entitySubscriptionsQueryOptions(referenceId ?? ''),
    enabled: !!referenceId,
  })

  // Only the shipment view (referenceId) fetches subscriptions, so only it needs the opt-out filter.
  const preferencesQuery = useQuery({
    ...currentCustomerPreferencesQueryOptions(),
    enabled: !!referenceId,
  })

  const disabledUserIds = useMemo(() => {
    const disabled = new Set<string>()
    for (const preference of preferencesQuery.data ?? []) {
      if (preference.preferences.some((entry) => entry.instantEmail === false)) {
        disabled.add(preference.user.id)
      }
    }
    return disabled
  }, [preferencesQuery.data])

  // Fail closed: only trust the subscription list once preferences resolve, so opted-out users are
  // never shown while preferences are still loading or errored.
  const receivers = useMemo(
    () =>
      deriveAcknowledgementReceivers({
        subscriptions: preferencesQuery.isSuccess ? subscriptionsQuery.data : undefined,
        bookingRecipients,
        disabledUserIds,
      }),
    [preferencesQuery.isSuccess, subscriptionsQuery.data, bookingRecipients, disabledUserIds]
  )

  return {
    receivers,
    isLoading: subscriptionsQuery.isLoading || preferencesQuery.isLoading,
    isError: subscriptionsQuery.isError || preferencesQuery.isError,
  }
}
